import React, { useMemo } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useSystemsContext, SystemSection } from "../../context/SystemsContext";
import { getFormSectionStyles } from "./forms/formSectionStyles";

import AssetProcessingForm from "./forms/AssetProcessing/AssetProcessingForm";
import AssetStorageForm from "./forms/AssetStorage/AssetStorageForm";
import AssetSyndicationForm from "./forms/AssetSyndication/AssetSyndicationForm";
import ClientProcessForm from "./forms/ClientProcessForm";

type ConfigSection = "clientProcess" | "assetProcessing" | "assetStorage" | "assetSyndication";

const sectionLabels: Record<ConfigSection, string> = {
  clientProcess: "Client Process",
  assetProcessing: "Asset Processing",
  assetStorage: "Asset Storage",
  assetSyndication: "Asset Syndication",
};

const sectionDescriptions: Record<ConfigSection, string> = {
  clientProcess: "Tablet, VIN, capture, and workflow settings used by the operator-facing client.",
  assetProcessing: "Pipeline steps that run on captured assets before they are delivered.",
  assetStorage: "Where assets land after processing and how merging / archival is handled.",
  assetSyndication: "Downstream destinations and partner integrations for finished assets.",
};

const ConfigDetails: React.FC = () => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);

  const {
    formData: system,
    selectedConfig: expandedConfigId,
    selectedSection,
    handleChange,
  } = useSystemsContext();

  if (!Object.keys(sectionLabels).includes(selectedSection as SystemSection)) {
    return null;
  }
  const section = selectedSection as ConfigSection;

  const selectedConfig = useMemo(() => {
    if (!system?.configurations || !expandedConfigId) return null;
    return system.configurations[expandedConfigId];
  }, [system, expandedConfigId]);

  if (!selectedConfig) {
    return (
      <Typography variant="body1" sx={{ mt: 2 }}>
        No configuration selected.
      </Typography>
    );
  }

  const label = sectionLabels[section];
  const description = sectionDescriptions[section];
  const sectionData = selectedConfig?.[section] || {};
  const configName = selectedConfig?.clientProcess?.name || "Unnamed Config";
  const isDefault = Boolean(
    selectedConfig?.clientProcess?.is_default || selectedConfig?.is_default,
  );

  const handleFieldChange = (path: string, value: any) => {
    const fullPath = `configurations.${expandedConfigId}.${section}.${path}`;
    handleChange(fullPath, value);
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={0.75}>
        <Typography sx={styles.eyebrow}>{label}</Typography>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              letterSpacing: "-0.01em",
              fontSize: { xs: "1.5rem", md: "1.8rem" },
            }}
          >
            {configName}
          </Typography>
          {isDefault && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "999px",
                fontSize: "11px",
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: styles.accent,
                border: `1px solid ${styles.accent}40`,
                backgroundColor: `${styles.accent}14`,
              }}
            >
              Default
            </Box>
          )}
        </Stack>
        {description && (
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 720 }}>
            {description}
          </Typography>
        )}
      </Stack>

      {section === "clientProcess" && (
        <ClientProcessForm config={sectionData} handleChange={handleFieldChange} />
      )}
      {section === "assetProcessing" && (
        <AssetProcessingForm config={sectionData} handleChange={handleFieldChange} />
      )}
      {section === "assetSyndication" && (
        <AssetSyndicationForm config={sectionData} handleChange={handleFieldChange} />
      )}
      {section === "assetStorage" && (
        <AssetStorageForm config={sectionData} handleChange={handleFieldChange} />
      )}
    </Stack>
  );
};

export default ConfigDetails;
